import { ENTITY, UserActions } from '@/enums';
import useCheckScopeCombinations from '@/hooks/useCheckScopeCombinations';
import { userService } from '@/services/api-services';
import {
  useInfiniteQuery,
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import { newQueryKeys, QueryKeyTypes } from './queryKeys';

import { SignedUrlData, UserEntity } from '@/interfaces';
import {
  AdminSettingEntity,
  AdminSettingParams,
  UserParams,
} from '@/pages/privatePages/user/types';
import { CustomResponse } from '@/services/api-services/type';
import flatMap from 'lodash/flatMap';
import { CustomInfiniteQueryOptions } from './type';
import { passPageParams } from './utils';

export type SignedUrlPayload = { filename: string; filetype: string };
export type GenerateManySignedUrlsPayload = {
  payload: SignedUrlPayload;
  originFileObj: any;
};

export type StoreUploadedTermsOfServicePayload = UseMutationOptions<
  any,
  Error,
  string
>;

export type GetPresignedUrlPayload = UseMutationOptions<any, Error, string>;

type MutationUploadFile = UseMutationOptions<
  SignedUrlData,
  Error,
  SignedUrlPayload
>;

type GetAdminSettingQuery = Partial<
  UseQueryOptions<
    AdminSettingEntity,
    Error,
    AdminSettingEntity,
    QueryKeyTypes<AdminSettingParams>
  >
>;
export const useGetAdminSettingQuery = (
  props: { params?: AdminSettingParams; options?: GetAdminSettingQuery } = {},
) => {
  const { options = {}, params = {} } = props;

  const query = useQuery({
    queryKey: newQueryKeys.adminSetting.list(params),
    queryFn: userService.getAdminSettings,
    ...options,
  });

  return query;
};

export const useUploadTermsOfServiceMutation = (
  options: MutationUploadFile,
) => {
  const { onSuccess, onError, ...rest } = options;

  const mutation = useMutation({
    mutationFn: userService.getSignedUrl,
    onSuccess(data, file, context) {
      onSuccess?.(data, file, context);
    },
    onError(error, file, context) {
      onError?.(error, file, context);
    },
    ...rest,
  });

  return mutation;
};

export const useStoreTermsOfServicePathMutation = (
  options: StoreUploadedTermsOfServicePayload,
) => {
  const { onSuccess, onError, ...rest } = options;

  const mutation = useMutation({
    mutationFn: userService.storeTermsOfServicePath,
    onSuccess(data, path, context) {
      onSuccess?.(data, path, context);
    },
    onError(error, path, context) {
      onError?.(error, path, context);
    },
    ...rest,
  });

  return mutation;
};

export const useGetPresignedUrlMutation = (options: GetPresignedUrlPayload) => {
  const { onSuccess, onError, ...rest } = options;

  const mutation = useMutation({
    mutationFn: userService.getPresignedUrl,
    onSuccess(data, path, context) {
      onSuccess?.(data, path, context);
    },
    onError(error, path, context) {
      onError?.(error, path, context);
    },
    ...rest,
  });

  return mutation;
};

type InfiniteQuery = CustomInfiniteQueryOptions<
  CustomResponse<UserEntity[]>,
  UserParams
>;
export const useGetInfiniteUsers = (props: {
  params: UserParams;
  options?: InfiniteQuery;
}) => {
  const { params, options = {} } = props;
  const queryKey = newQueryKeys.user.list(passPageParams(params));
  const { canGetUsers } = useCheckScopeCombinations(
    [[{ entity: ENTITY.USERS, action: UserActions.GET }]],
    { optimistic: false },
  );

  const query = useInfiniteQuery({
    queryKey,
    queryFn: userService.getInfinite,
    initialPageParam: 1,
    getNextPageParam: lastPage =>
      lastPage.hasNextPage ? lastPage.page + 1 : undefined,
    ...options,
    enabled: (options.enabled ?? true) && canGetUsers,
  });

  const flatData = flatMap(query?.data?.pages?.map(p => p.data));

  return { ...query, flatData };
};

type DetailQuery = Partial<
  UseQueryOptions<UserEntity, Error, UserEntity, QueryKeyTypes>
>;
export const useGetUserProfileById = (props: {
  id: string;
  options?: DetailQuery;
}) => {
  const { id, options = {} } = props;
  const { canGetUsers } = useCheckScopeCombinations(
    [[{ entity: ENTITY.USERS, action: UserActions.GET }]],
    { optimistic: false },
  );
  const query = useQuery({
    queryKey: newQueryKeys.user.detail(id),
    queryFn: userService.getById,
    ...options,
    enabled: (options.enabled ?? true) && canGetUsers,
  });

  return query;
};
